import { useEffect, useMemo, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import { times } from 'lodash'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'

import { Alert, Button, Card, CardColors, Dialog, Form, Text } from '@/ui'
import {
  getFieldNames,
  makeReqStringSchema,
  makeStringArraySchema,
} from '@/base'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  bulkUpdateUserIncomeMonths,
  fetchUserIncomePlans,
  selectUserIncomePlanById,
  selectUserIncomePlans,
} from '@/features/income-planning/incomePlanning.slice'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'

export const ApplyPlanDialog = ({
  selectedMonths,
  setSelectedMonths,
  selectedYear,
}: {
  selectedMonths: number[]
  setSelectedMonths: (months: number[]) => void
  selectedYear: number
}) => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)

  const currentDate = useMemo(() => DateTime.now(), [])

  useEffect(() => {
    dispatch(fetchUserIncomePlans())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      selectedMonths: selectedMonths.map((month) => `${selectedYear}-${month}`),
      planId: null,
    },
    enableReinitialize: true,
    onSubmit: async ({ planId, selectedMonths }) => {
      if (!planId) {
        return
      }

      await dispatch(
        bulkUpdateUserIncomeMonths({
          planId,
          months: selectedMonths.map((month) => ({
            month: Number(month.split('-')[1]),
            year: Number(month.split('-')[0]),
          })),
        })
      )
      setOpen(false)
      setSelectedMonths([])
    },
  })

  const incomePlans = useReselector(selectUserIncomePlans)

  const selectedMonthOptions = useMemo(() => {
    // Flat map used so months not separated out by year
    return times(2).flatMap((yearOffset) => {
      const year = currentDate.year + yearOffset

      return times(12).map((iMonth) => {
        const month = iMonth + 1
        return {
          value: `${year}-${month}`,
          text: DateTime.fromObject({ month, year }).toFormat(
            DATE_FORMATS_LUXON.MONTH_YEAR_LONG
          ),
        }
      })
    })
  }, [currentDate.year])

  const selectedPlanOptions = useMemo(
    () =>
      Object.values(incomePlans).map((plan) => ({
        value: plan.id,
        text: plan.planName,
      })),
    [incomePlans]
  )

  const { submitForm, values } = formik
  const fieldNames = getFieldNames(formik)

  const selectedPlan = useReselector(selectUserIncomePlanById, values.planId)

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <FormikProvider value={formik}>
        <Dialog.Trigger asChild>
          <Button
            iconLeft={light('copy')}
            variant="secondary"
            onClick={() => setOpen(true)}
          >
            {selectedMonths.length
              ? `Apply Plan to ${selectedMonths.length} month${selectedMonths.length > 1 ? 's' : ''}`
              : 'Apply Plan'}
          </Button>
        </Dialog.Trigger>
        <Dialog.Content>
          <Dialog.Title>Apply plan</Dialog.Title>
          <Dialog.Description>
            Select which plan you’d like to apply to the selected month(s).
          </Dialog.Description>
          <Alert
            title="Applying a plan to the selected months will overwrite their existing values with new values."
            className="mt-6"
          />
          <div className="mt-6">
            <Form.Multiselect
              label="Apply to"
              options={selectedMonthOptions}
              name={fieldNames.selectedMonths}
              schema={makeStringArraySchema()}
            />
          </div>
          <div className="mt-6">
            <Form.Select
              label="Select"
              options={selectedPlanOptions}
              name={fieldNames.planId}
              schema={makeReqStringSchema()}
            />
          </div>
          {selectedPlan && (
            <Card color={CardColors.LEVEL_1}>
              <Text variant={TypographyStyles.LABEL_MD}>
                {selectedPlan.planName}
              </Text>
              <Text variant={TypographyStyles.DISPLAY_SM} className="mt-6">
                {formatCurrencyFromCents(
                  selectedPlan.totalPlannedIncomeInCents
                )}
              </Text>
              <Text variant={TypographyStyles.LABEL_SM}>
                Total Planned Income
              </Text>
              <Text variant={TypographyStyles.DISPLAY_SM} className="mt-4">
                {formatCurrencyFromCents(selectedPlan.therapyIncomeInCents)}
              </Text>
              <Text variant={TypographyStyles.LABEL_SM}>Therapy Income</Text>
              <Text variant={TypographyStyles.DISPLAY_SM} className="mt-6">
                {formatCurrencyFromCents(selectedPlan.ratePerSession)}
              </Text>
              <Text variant={TypographyStyles.LABEL_SM}>
                Blended per Session Rate
              </Text>
              <Text variant={TypographyStyles.DISPLAY_SM} className="mt-6">
                {selectedPlan.sessionsPerMonth}
              </Text>
              <Text variant={TypographyStyles.LABEL_SM}>
                Sessions per Month
              </Text>
            </Card>
          )}
          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </Dialog.Close>
            <Dialog.Close asChild>
              <Button onClick={submitForm}>Apply</Button>
            </Dialog.Close>
          </Dialog.Footer>
        </Dialog.Content>
      </FormikProvider>
    </Dialog>
  )
}
